import { cached, tracked } from '@glimmer/tracking';

import {
	BlockVisualization,
	ChartLegendPosition,
	DataSelectType,
	OrderDirection,
	ShownLabels,
	SortFieldType,
} from 'webapp/enums/enums';
import { glueDimensionValues } from 'webapp/utils/common';
import { hashCode } from 'webapp/utils/number';

import type { AnyVisualization } from 'webapp/models-custom/block-setup';
import type DataSelect from 'webapp/models-custom/data-select';
import DateRange from 'webapp/models-custom/date-range';
import DsCompare from 'webapp/models-custom/ds-compare';
import type DsDataOutputCompare from 'webapp/models-custom/ds-data/output/compare';
import type DsDataOutputTopLevel from 'webapp/models-custom/ds-data/output/top';
import type { MetricSelectionData } from 'webapp/models-custom/ds-data/output/top';
import VisualizationBase from 'webapp/models-custom/visualization/base';
import DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';
import type { SeriesData } from 'webapp/d-chart/series/series';
import DChartSeries from 'webapp/d-chart/series/series';

export interface CategoryData {
	id: string;
	label: string;
}

const RecordLimit = 100;

export default class VisualizationHorizontalBarChart extends VisualizationBase {
	visualizationType = BlockVisualization.HORIZONTAL_BAR_CHART;

	recordLimit = RecordLimit;

	// -------- visualization settings ---------

	override get showLegendPosition(): boolean {
		return this.blockSetup.dataSelectsShown.length > 1;
	}

	get isLegendOff(): boolean {
		return this.block.legendPosition === ChartLegendPosition.OFF;
	}

	get isLegendBottom(): boolean {
		return !this.isLegendOff;
	}

	get isStacked(): boolean {
		return this.blockSetup.dataSelectsShown.every((ds) => ds.isStacked);
	}

	override get enabledSortOptions(): LabelValuePairString[] {
		const options = Array.from(super.enabledSortOptions).filter((o) => {
			return o.value !== SortFieldType.CHANGE;
		});
		return options;
	}

	// ------ Data ------

	@tracked series: DChartSeries[] = [];
	@tracked categories: CategoryData[] = [];

	override updateDataCache() {
		// together bcos of sort
		const { categories, series } = this.getCategoriesAndSeries();
		this.categories = categories;
		this.series = series;
		this.updateColorIndexes();
	}

	override clearDataCache() {
		super.clearDataCache();
		this.series = [];
		this.categories = [];
	}

	updateColorIndexes() {
		const mainSeries = this.series.filter((s) => s.isMain);
		this.updateColorIndexesForItems(mainSeries);

		const mainSeriesCount = mainSeries.length;
		const secondarySeries = this.series.filter((s) => !s.isMain);
		secondarySeries.forEach((s) => {
			if (s.primarySeries && mainSeriesCount > 1) {
				s.colorIndexOrClass = s.primarySeries.colorIndexOrClass;
			}
		});
	}

	@cached
	get outputsByMetricAndUnit() {
		const outputGroups: {
			dataSelect: DataSelect;
			unit: string;
			outputs: DsDataOutputTopLevel[];
		}[] = [];

		this.blockSetup.sortedDataSelectsShown.forEach((dataSelect) => {
			const dataSelectId = dataSelect.id!;
			dataSelect.outputs.forEach((output) => {
				const unit = output.unit;
				const existingGroup = outputGroups.find((g) => {
					return g.dataSelect.id === dataSelectId && g.unit === unit;
				});
				if (existingGroup) {
					existingGroup.outputs.push(output);
				} else {
					outputGroups.push({
						dataSelect,
						unit,
						outputs: [output],
					});
				}
			});
		});

		return outputGroups;
	}

	generateCategoryIdFromMetricSelectionData(metricSelectionData: MetricSelectionData | null) {
		// for selections without attribute, we assume it's a non-dimension metric
		// generating unique id for each such bcos we want to show them in seperate rows
		let categoryId = '';
		if (metricSelectionData) {
			// value-only so the same dimension value joins across metrics even when the key differs
			const dimensions =
				glueDimensionValues(metricSelectionData.dimensionValues) ||
				`dbox__ds-${metricSelectionData?.dsId}`;
			const unit = metricSelectionData.unit;

			categoryId = unit ? `${dimensions}__${unit}` : dimensions;
		}
		return categoryId;
	}

	private getCategoriesAndSeries(): { categories: CategoryData[]; series: DChartSeries[] } {
		const distinctUnits = new Set<string | undefined>();
		this.blockSetup.sortedDataSelectsShown.forEach((ds) => {
			ds.outputs.forEach((output) => {
				distinctUnits.add(output.unit);
			});
		});
		const showUnitInLabel = distinctUnits.size > 1;

		const categories: CategoryData[] = [];
		this.blockSetup.sortedDataSelectsShown.forEach((ds) => {
			ds.outputs.forEach((output) => {
				const categoryId = this.generateCategoryIdFromMetricSelectionData(
					output.metricSelectionData,
				);
				if (!categories.find((c) => c.id === categoryId)) {
					const attributeNames = output.attributeNamesString;
					const categoryLabel = attributeNames
						? output.unit && showUnitInLabel
							? `${attributeNames} (${output.unit})`
							: attributeNames
						: output.metricName;
					categories.push({
						id: categoryId,
						label: categoryLabel,
					});
				}
			});
		});

		const series: DChartSeries[] = [];

		this.outputsByMetricAndUnit.forEach(({ dataSelect, unit: primaryUnit, outputs }) => {
			const points: DChartPrimaryPoint[] = [];
			outputs.forEach((o) => {
				points.push(...this.createDChartPointsFromOutput(o));
			});

			// these are all from same ds and unit, just different attributes
			const firstOutput = outputs[0];

			if (!firstOutput) {
				return;
			}

			const metricSelectionData: MetricSelectionData = Object.assign(
				{},
				firstOutput.metricSelectionData || {},
			);
			metricSelectionData.dimensionValues = {};

			const seriesData: SeriesData = {
				type: dataSelect.type,
				timezone: this.dataTimezone,
				title: firstOutput.titleWithoutAttribute,
				points,
				typeToDraw: 'bar',
				isStacked: dataSelect.isStacked,
				showDataLabels: dataSelect.shownLabels === ShownLabels.VALUE,
				metricSelectionData,
			};

			const primarySeries = new DChartSeries(seriesData);
			series.push(primarySeries);

			// each compare root will be different series for sure
			// also each diff unit
			dataSelect.dataRoot?.compareRoots.forEach((compareRoot) => {
				const outputGroups: {
					unit: string;
					outputs: DsDataOutputCompare[];
				}[] = [];

				compareRoot.outputs.forEach((output) => {
					const unit = output.unit;
					if (unit !== primaryUnit) {
						return;
					}

					const existingGroup = outputGroups.find((g) => {
						return g.unit === unit;
					});
					if (existingGroup) {
						existingGroup.outputs.push(output);
					} else {
						outputGroups.push({
							unit,
							outputs: [output],
						});
					}
				});

				let titlePrefix = '';
				const compareSettings = compareRoot.compareSettings;
				if (compareSettings) {
					const dateRange = DateRange.createFromFormula(dataSelect.dateRange),
						dsCompare = DsCompare.createFromFormula(
							compareSettings,
							dateRange,
							dataSelect.metricDefaults,
						);
					titlePrefix = dsCompare.titleUX;
				}

				outputGroups.forEach(({ outputs }) => {
					// these are all from same ds and unit, just different attributes
					const firstOutput = outputs[0],
						type = DataSelectType.COMPARE;

					if (!firstOutput) {
						return;
					}

					const points: DChartPrimaryPoint[] = [];
					outputs.forEach((o) => {
						o.points.forEach((p) => {
							const point = this.createDChartPointFromPoint(o, p);
							point.metricSelectionData = {
								dsId: dataSelect.numericId!, // we want them matched to the primary if generateCategoryIdFromMetricSelectionData is used
								primaryDsId: null,
								metricSetupData: null,
								metricName: '',
								unit: o.unit,
								dimensionValues: o.dimensionValues,
							};
							points.push(point);
						});
					});

					const seriesData: SeriesData = {
						type,
						timezone: this.dataTimezone,
						title: firstOutput.titleWithoutAttribute,
						titlePrefix,
						points,
						typeToDraw: 'bar',
						isStacked: primarySeries.isStacked,
						showDataLabels: primarySeries.showDataLabels,
						metricSelectionData: {
							dsId: 0,
							primaryDsId: null,
							metricSetupData: null,
							metricName: '',
							unit: firstOutput.unit,
							dimensionValues: {},
						},
					};

					const serie = new DChartSeries(seriesData);
					serie.primarySeries = primarySeries;
					series.push(serie);
				});
			});
		});

		// sorting categories (not series!)
		const sortField = this.block.sortField || SortFieldType.VALUE,
			sortOrder = this.block.sortOrder || OrderDirection.DESC;

		// just sort category names
		const sumsByCategory = categories.map((c) => {
			return { label: c.id, value: 0 };
		});

		if (sortField === SortFieldType.VALUE) {
			// fill sumsByCategory with values only if we have value sort
			series
				.filter((s) => s.isMain)
				.forEach((s) => {
					s.points.forEach((p) => {
						const categoryId = this.generateCategoryIdFromMetricSelectionData(
								p.metricSelectionData,
							),
							addTo = sumsByCategory.find((g) => g.label === categoryId);
						if (addTo) {
							addTo.value += p.value || 0;
						}
					});
				});
		}

		categories.sort((a, b) => {
			let result = 0,
				mustApplyReverse = sortOrder === OrderDirection.DESC;
			switch (sortField) {
				case SortFieldType.CUSTOM:
					return 0; // dont apply sortorder
				case SortFieldType.NAME:
					result = a.id.localeCompare(b.id);
					break;
				case SortFieldType.VALUE: {
					const aSum = sumsByCategory.find((g) => g.label === a.id)?.value || null,
						bSum = sumsByCategory.find((g) => g.label === b.id)?.value || null;
					result = DChartPrimaryPoint.doValueCompare(aSum, bSum);
					// if any of the values is null (missing), it must be always on bottom
					// doValueCompare already does so, which means we must skip applying DESC sort
					if (aSum === null || bSum === null) {
						mustApplyReverse = false;
					}
					break;
				}
			}
			if (mustApplyReverse) {
				// reverse the result
				if (result > 0) {
					result = -1;
				} else if (result < 0) {
					result = 1;
				}
			}
			return result;
		});

		return {
			categories,
			series,
		};
	}

	// ----------- Detecting possible rendering changes -----------

	override get visualizationSettingsHash(): number {
		const block = this.block,
			dss = this.blockSetup.dataSelects;
		return hashCode(
			JSON.stringify({
				l: block.legendPosition,
				sf: block.sortField,
				so: block.sortOrder,
				bc: block.colors,
				st: dss.map((ds) => ds.isStacked),
				sv: dss.map((ds) => ds.shownLabels),
				o: dss.map((ds) => ds.order), // bcos of possible custom sort
			}),
		);
	}

	// ------- general helpers --------

	get selectedSortDataSelectId(): number | null {
		// not necessarily what's saved, bcos this has fallback to first
		const firstDataSelect = this.blockSetup.firstShownDataSelect;
		return firstDataSelect?.sortDataSelect || firstDataSelect?.numericId || null;
	}

	get sortDataSelect() {
		// the one that is actually saved
		const dataSelects = this.blockSetup.dataSelects;
		const sortDataSelectId = dataSelects.find((item) => item.sortDataSelect)?.sortDataSelect;
		return sortDataSelectId
			? dataSelects.find((ds) => ds.numericId === sortDataSelectId)
			: undefined;
	}

	setSortDataSelectFrom(sortDataSelectId: number | null): void {
		this.blockSetup.dataSelects.forEach((ds) => {
			if (ds.numericId === sortDataSelectId) {
				ds.sortDataSelect = null;
			} else {
				ds.sortDataSelect = sortDataSelectId;
			}
			this.blockSetup.saveDataSelect(ds);
		});
	}

	override onChangeMetricInDataSelect(dataSelect: DataSelect): void {
		const sortDataSelectId = this.sortDataSelect?.numericId;
		if (sortDataSelectId) {
			if (sortDataSelectId !== dataSelect.numericId) {
				dataSelect.sortDataSelect = sortDataSelectId;
			}
		} else {
			this.setSortDataSelectFrom(dataSelect.numericId);
		}
	}

	override onCreatePrimaryMainDataSelect(dataSelect: DataSelect): void {
		super.onCreatePrimaryMainDataSelect(dataSelect);

		// if needed, also set stacked on this DS
		const otherDs = Array.from(this.blockSetup.dataSelectsShown).filter((ds) => {
			return ds.model !== dataSelect.model;
		});

		if (otherDs.length && otherDs.every((ds) => ds.isStacked)) {
			dataSelect.isStacked = true;
		}

		// add sortDataSelect
		let sortDataSelectId = this.sortDataSelect?.numericId;
		if (!sortDataSelectId) {
			sortDataSelectId = this.blockSetup.firstShownDataSelect?.numericId || null;
		}
		dataSelect.sortDataSelect = sortDataSelectId;
	}

	override onDeleteDataSelect(dataSelect: DataSelect): void {
		super.onDeleteDataSelect(dataSelect);

		const id = dataSelect.numericId;
		if (id) {
			// change sortDataSelect that were pointing to this deleted DS
			const dataSelectsSortedByThis = this.blockSetup.dataSelects.filter(
				(item) => item.sortDataSelect === id,
			);
			if (dataSelectsSortedByThis.length) {
				this.setSortDataSelectFrom(this.blockSetup.firstShownDataSelect?.numericId || null);
				dataSelectsSortedByThis.forEach((ds) => {
					this.blockSetup.saveDataSelect(ds);
				});
			}
		}
	}

	// ------- visualization switch helpers --------

	override preCleanupForSwitchTo(visualizationTo: AnyVisualization): void {
		super.preCleanupForSwitchTo(visualizationTo);

		// clear sortDataSelect
		this.loggerService.logToConsole('[Vis change] from Horiz: clear sortDataSelect');
		this.setSortDataSelectFrom(null);
	}

	override updateForSwitchFrom(visualizationFrom: AnyVisualization): void {
		super.updateForSwitchFrom(visualizationFrom);

		// add SortDataSelect if we have an aligible metric
		const sortDataSelectId = this.selectedSortDataSelectId;
		if (sortDataSelectId) {
			this.loggerService.logToConsole('[Vis change] to Horiz: set sortDataSelect');
			this.setSortDataSelectFrom(sortDataSelectId);
		}
	}

	// ------- MISC --------

	override getAllMainPoints() {
		const points: DChartPrimaryPoint[] = [];

		this.series
			.filter((serie) => serie.isMain)
			.forEach((serie) => {
				points.push(...serie.points);
			});

		return points;
	}

	static override recordLimit = RecordLimit;
}
